import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchTeamMembers } from '../lib/cms';
import { Users, Phone, Mail, ArrowLeft, Shield, CheckCircle2 } from 'lucide-react';

export default function EmployeeDetail() {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadMember() {
      setLoading(true);
      const team = await fetchTeamMembers();
      const found = team.find(m => m.id === id || m.slug?.da === id);
      setMember(found || null);
      setLoading(false);
    }
    loadMember();
  }, [id]);

  // Update SEO metadata
  useEffect(() => {
    if (member) {
      const roleText = member.role?.da || member.role || 'Medarbejder';
      document.title = `${member.name} - ${roleText} - D.E. TekniQ K/S`;
      
      let metaTag = document.querySelector('meta[name="description"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'description');
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', `Læs mere om ${member.name}, ${roleText} hos D.E. TekniQ K/S. Vi leverer professionelle el- og energiløsninger i Midtjylland.`);
    }
  }, [member]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-none border-4 border-navy-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold text-navy-950">Medarbejderen blev ikke fundet</h1>
        <p className="mt-4 text-graphite-600">Vi kunne desværre ikke finde den medarbejder, du leder efter.</p>
        <div className="mt-8">
          <Link
            to="/om-os"
            className="inline-flex items-center gap-2 rounded-none bg-navy-950 px-6 py-3 text-sm font-semibold text-white hover:bg-navy-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Tilbage til Om os
          </Link>
        </div>
      </div>
    );
  }

  const roleText = member.role?.da || member.role;

  return (
    <div className="bg-white">
      {/* Header Banner */}
      <div className="bg-navy-950 py-16 text-white md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#0B803A_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/om-os"
            className="inline-flex items-center gap-2 text-sm font-medium text-energy-400 hover:text-energy-300 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4" /> Tilbage til Om os
          </Link>
          <span className="block text-xs font-bold uppercase tracking-wider text-energy-400">
            Mød vores team
          </span>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl mt-2">
            {member.name}
          </h1>
          <p className="mt-2 text-lg text-navy-200">
            {roleText} hos D.E. TekniQ K/S
          </p>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            
            {/* Left Column: Image */}
            <div className="lg:col-span-5">
              <div className="w-64 h-64 bg-navy-50 overflow-hidden border border-navy-100 rounded-none mx-auto lg:mx-0">
                {member.image && member.image[0] ? (
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/${member.image[0].replace('https://assets.auxodev.dk/', '')}`}
                    alt={member.name}
                    className="h-full w-full object-cover rounded-none"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-navy-900 text-white rounded-none">
                    <Users className="h-20 w-20 text-energy-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Bio & Info */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Faglig Profil</span>
                <h2 className="mt-2 text-2xl font-bold text-navy-950 sm:text-3xl">
                  Baggrund & Kompetencer
                </h2>
                
                {member.bio?.da || member.bio ? (
                  <div 
                    className="mt-6 text-base text-graphite-700 leading-relaxed space-y-4 prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: member.bio?.da || member.bio }}
                  />
                ) : (
                  <p className="mt-6 text-base text-graphite-600 italic">
                    Der er endnu ikke tilføjet en detaljeret beskrivelse for denne medarbejder.
                  </p>
                )}
              </div>

              {/* Trust Badges */}
              <div className="mt-12 pt-8 border-t border-navy-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex gap-3 items-start">
                  <Shield className="h-5 w-5 text-energy-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-navy-950">Autoriseret el-arbejde</h4>
                    <p className="text-xs text-graphite-600 mt-1">Højeste standarder for sikkerhed og kvalitet.</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="h-5 w-5 text-energy-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-navy-950">Lokal forankring</h4>
                    <p className="text-xs text-graphite-600 mt-1">Vi kender Midtjylland og rykker hurtigt ud.</p>
                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
